import React, { useId } from 'react';
import { Alert, LoadingSpinner } from '@shared/components';
import { formatDateOnlyShort } from '@/app/utils/formatDate';
import type { ConfiguredService } from '../../../types/service.types';
import type { SmriqWorkItem } from '../../../types/smriq.types';
import { MAX_RECOMMENDED_OPERATION_EXCLUSIONS } from '../../../hooks/useSmriqDeclines';
import { smriqInclusionsLabel } from '../../../utils/smriqInclusionsLabel';
import { ServiceItem } from '../ServiceItem';
import { ProviderInclusionsPanel } from './ProviderInclusionsPanel';

interface SmriqVisitInclusions {
    items: SmriqWorkItem[];
    declinedItems: SmriqWorkItem[];
}

interface SmriqSchedulePanelProps {
    className?: string;
    services: ConfiguredService[];
    currency: string;
    /** Keyed by visit id (== `instanceId`); empty until the in-memory `generate`
     *  response behind the current schedule is known. */
    inclusionsByVisit?: Record<string, SmriqVisitInclusions>;
    isItemDeclined?: (visitId: string, itemId: string) => boolean;
    onToggleDecline?: (visitId: string, itemId: string) => void;
    isDeclineAllowed?: (visitId: string, itemId: string) => boolean;
    atExclusionLimit?: boolean;
    exclusionsSupported?: boolean;
    disableToggles?: boolean;
    /** The configurator's single loader: the initial/after-reconfirm retrieve or an in-flight
     *  regenerate. Takes precedence over the schedule and the empty state. */
    loading?: boolean;
    /** The applied price set's vendor description, from the generate behind this schedule. */
    pricingVariantDescription?: string | null;
    /** Plan-outcome messages; shown in place of the empty state, or above a stale schedule. */
    errors?: string[];
}

/** Renders `configuredServices` in the same ServiceItem cards as a local plan, with the
 *  decline toggles in each card's inclusions slot. No fetching of its own. */
export const SmriqSchedulePanel: React.FC<SmriqSchedulePanelProps> = ({
    className,
    services,
    currency,
    inclusionsByVisit = {},
    isItemDeclined = () => false,
    onToggleDecline = () => {},
    isDeclineAllowed = () => true,
    atExclusionLimit = false,
    exclusionsSupported = true,
    disableToggles = false,
    loading = false,
    pricingVariantDescription = null,
    errors = [],
}) => {
    const headingId = useId();

    return (
        <section aria-labelledby={headingId} className={`bg-mp-surface rounded-xl ${className ?? ''}`}>
            <div className="flex flex-wrap items-baseline gap-x-2 mb-2">
                <h3 id={headingId} className="text-base font-semibold text-mp-foreground">
                    Service Order
                </h3>
                {pricingVariantDescription && services.length > 0 && (
                    <span className="text-xs text-mp-muted">
                        {pricingVariantDescription}
                    </span>
                )}
            </div>

            {!loading && errors.length > 0 && (
                <Alert variant="error" className="mb-3">
                    {errors.map((message) => (
                        <p key={message}>{message}</p>
                    ))}
                </Alert>
            )}

            {loading ? (
                // The configurator's single loader: it stands in for the schedule (and the empty
                // state) while the plan is retrieved or regenerated.
                <div className="py-12">
                    <LoadingSpinner
                        size="xl"
                        message={services.length === 0 ? 'Retrieving plan…' : 'Updating plan…'}
                    />
                </div>
            ) : services.length === 0 ? (
                errors.length === 0 && (
                    <div
                        role="status"
                        className="text-center py-8 bg-mp-background border border-mp-border rounded-lg"
                    >
                        <p className="text-sm text-mp-muted">
                            Set a duration and press Generate plan to see the priced schedule.
                        </p>
                    </div>
                )
            ) : (
                <>
                    {exclusionsSupported && atExclusionLimit && !disableToggles && (
                        <p role="status" className="text-sm text-mp-warning mb-3">
                            You have declined the maximum of{' '}
                            {MAX_RECOMMENDED_OPERATION_EXCLUSIONS} operations. Re-include one before
                            declining another.
                        </p>
                    )}
                    <div className="space-y-4">
                        {services.map((service) => {
                            // Absent on a resumed quote with no generate this session: the card
                            // then renders the persisted items read-only, with no decline toggles.
                            const inclusions = inclusionsByVisit[service.instanceId];

                            return (
                                <ServiceItem
                                    key={service.instanceId}
                                    service={service}
                                    onRemove={() => {}}
                                    canRemove={false}
                                    currency={currency}
                                    // The declined operations are counted so the disclosure
                                    // stays reachable to re-include them, never as inclusions.
                                    inclusionsCount={
                                        inclusions
                                            ? inclusions.items.length + inclusions.declinedItems.length
                                            : undefined
                                    }
                                    inclusionsLabel={
                                        inclusions
                                            ? smriqInclusionsLabel(
                                                  inclusions.items.length,
                                                  inclusions.declinedItems.length,
                                              )
                                            : undefined
                                    }
                                    inclusionsDefaultOpen
                                    inclusionsSlot={
                                        inclusions ? (
                                            <ProviderInclusionsPanel
                                                items={inclusions.items}
                                                declinedItems={inclusions.declinedItems}
                                                visitId={service.instanceId}
                                                visitLabel={`${service.name} due ${formatDateOnlyShort(service.startDate)}`}
                                                isDeclined={(itemId) =>
                                                    isItemDeclined(service.instanceId, itemId)
                                                }
                                                onToggleDecline={(itemId) =>
                                                    onToggleDecline(service.instanceId, itemId)
                                                }
                                                isDeclineAllowed={(itemId) =>
                                                    isDeclineAllowed(service.instanceId, itemId)
                                                }
                                                exclusionsSupported={exclusionsSupported}
                                                disabled={disableToggles}
                                                currency={currency}
                                            />
                                        ) : undefined
                                    }
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </section>
    );
};
